<template>
  <div class="map-container">
    <div id="communities-map" style="height: 400px;">
      <div class="map-controls">
        <div class="map-type-control">
          <div class="map-type-switch">
            <button
              @click="toggleMapType('map')"
              :class="['switch-btn', mapType === 'map' ? 'active' : '']"
            >
              Map
            </button>
            <button
              @click="toggleMapType('satellite')"
              :class="['switch-btn', mapType === 'satellite' ? 'active' : '']"
            >
              Satellite
            </button>
          </div>
        </div>
      </div>
      <div class="fullscreen-control">
        <button @click="toggleFullscreen" class="control-btn" title="Fullscreen">
          <i class="fas" :class="isFullscreen ? 'fa-compress' : 'fa-expand'"></i>
        </button>
      </div>
      <div class="bottom-controls">
        <!-- <button
          @click="toggleStreetView"
          class="control-btn street-view-btn"
          :class="{ 'disabled': !selectedMarkerPosition }"
          :disabled="!selectedMarkerPosition"
          title="Street View"
        >
          <i class="fas fa-street-view"></i>
        </button> -->
        <div class="zoom-controls">
          <button @click="zoomIn" class="control-btn zoom-btn">
            <i class="fas fa-plus"></i>
          </button>
          <div class="zoom-divider"></div>
          <button @click="zoomOut" class="control-btn zoom-btn">
            <i class="fas fa-minus"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

export default {
  name: 'CommunitiesLocationMap',
  props: {
    communities: {
      type: Array,
      required: true,
      validator: function(value) {
        return value.every(community =>
          community.position &&
          typeof community.position.lat === 'number' &&
          typeof community.position.lng === 'number' &&
          !isNaN(community.position.lat) &&
          !isNaN(community.position.lng)
        )
      }
    }
  },
  data() {
    return {
      map: null,
      markers: [],
      bounds: null,
      defaultZoom: 2,
      customIcons: new Map(),
      mapType: 'map',
      isFullscreen: false,
      currentTileLayer: null,
      selectedMarkerPosition: null
    }
  },
  mounted() {
    this.initMap()
  },
  methods: {
    initMap() {
      // Delete existing map instance if it exists
      if (this.map) {
        this.map.remove()
      }

      // Create map instance
      this.map = L.map('communities-map', {
        center: [0, 0],
        zoom: this.defaultZoom,
        zoomControl: false,  // Disable default zoom control since we have our own
        dragging: true,     // Enable map dragging
        scrollWheelZoom: false,  // Disable zoom with mouse wheel
        doubleClickZoom: false,  // Disable zoom on double click
        touchZoom: false,     // Disable zoom on mobile touch
        attributionControl: false  // Hide attribution message
      });

      // Add OpenStreetMap tiles
      this.currentTileLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19
      }).addTo(this.map);

      this.bounds = L.latLngBounds()
      this.updateMarkers()
    },
    createCustomIcon(color) {
      const svgTemplate = `
        <svg width="24" height="36" viewBox="0 0 24 36" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 0C5.383 0 0 5.383 0 12c0 9 12 24 12 24s12-15 12-24c0-6.617-5.383-12-12-12zm0 16c-2.206 0-4-1.794-4-4s1.794-4 4-4 4 1.794 4 4-1.794 4-4 4z" fill="${color}"/>
        </svg>
      `
      const svgUrl = 'data:image/svg+xml;base64,' + btoa(svgTemplate)

      return L.icon({
        iconUrl: svgUrl,
        iconSize: [24, 36],
        iconAnchor: [12, 36],
        popupAnchor: [0, -36]
      })
    },
    toggleMapType(type) {
      if (this.mapType !== type && this.map) {
        this.mapType = type;

        // Remove current tile layer
        if (this.currentTileLayer) {
          this.map.removeLayer(this.currentTileLayer);
        }

        const center = this.map.getCenter();
        const zoom = this.map.getZoom();

        // Add new tile layer based on type
        if (type === 'satellite') {
          this.currentTileLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
            maxZoom: 19
          });
        } else {
          this.currentTileLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19
          });
        }

        this.currentTileLayer.addTo(this.map);
        this.map.setView(center, zoom, { animate: false });
      }
    },
    toggleFullscreen() {
      const mapElement = document.getElementById('communities-map');
      if (!document.fullscreenElement) {
        mapElement.requestFullscreen();
        this.isFullscreen = true;
      } else {
        document.exitFullscreen();
        this.isFullscreen = false;
      }
      setTimeout(() => this.map.invalidateSize(), 100);
    },
    toggleStreetView() {
      if (this.selectedMarkerPosition) {
        const { lat, lng } = this.selectedMarkerPosition;
        window.open(`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`, '_blank');
      }
    },
    zoomIn() {
      this.map.zoomIn();
    },
    zoomOut() {
      this.map.zoomOut();
    },
    updateMarkers() {
      // Clear existing markers
      this.markers.forEach(marker => marker.remove())
      this.markers = []
      this.bounds = L.latLngBounds()
      this.selectedMarkerPosition = null

      this.communities.forEach(community => {
        if (community.position && !isNaN(community.position.lat) && !isNaN(community.position.lng)) {
          const position = L.latLng(community.position.lat, community.position.lng)

          // Use safe navigation for nested properties
          const color = (community.customSettings && community.customSettings.primaryBtnBgColor)
            ? community.customSettings.primaryBtnBgColor
            : '#FF0000'

          let icon = this.customIcons.get(color)
          if (!icon) {
            icon = this.createCustomIcon(color)
            this.customIcons.set(color, icon)
          }

          const marker = L.marker(position, { icon })
            .bindPopup(`
              <strong>${community.name || 'Unnamed Community'}</strong><br>
              ${(community.location && community.location.city) ? community.location.city + ', ' : ''}
              ${(community.location && community.location.state) ? community.location.state + ', ' : ''}
              ${(community.location && community.location.country) ? community.location.country : ''}
            `)
            .on('click', () => {
              this.selectedMarkerPosition = position;
            })
            .addTo(this.map)

          this.markers.push(marker)
          this.bounds.extend(position)
        }
      })

      // Add click handler to map to clear selection when clicking elsewhere
      this.map.on('click', (e) => {
        if (!this.markers.some(marker => marker.getLatLng().equals(e.latlng))) {
          this.selectedMarkerPosition = null;
        }
      });

      // Only fit bounds if we have valid markers
      if (this.markers.length > 0) {
        this.map.fitBounds(this.bounds, {
          padding: [50, 50],
          maxZoom: 12
        })
      }
    }
  },
  watch: {
    communities: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          if (this.map) {
            this.updateMarkers()
          }
        })
      }
    }
  },
  beforeDestroy() {
    if (this.map) {
      this.map.remove()
    }
  }
}
</script>

<style>
.map-container {
  position: relative;
  width: 100%;
  height: 400px;
  font-family: Arial, sans-serif;
  border-radius: 4px;
  overflow: hidden;
}

.map-controls {
  position: absolute;
  z-index: 1000;
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 10px;
  pointer-events: none;
}

.map-type-control {
  pointer-events: auto;
  margin-left: 10px;
}

.map-type-switch {
  display: flex;
  background: white;
  border-radius: 4px;
  padding: 2px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.switch-btn {
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border: none;
  background: transparent;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 3px;
}

.switch-btn.active {
  background: #fff;
  color: #333;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.switch-btn:not(.active) {
  background: #f8f9fa;
}

.switch-btn:hover:not(.active) {
  background: #f1f1f1;
}

.fullscreen-control {
  position: absolute;
  top: 15px;
  right: 10px;
  pointer-events: auto;
  z-index: 1000;
}

.bottom-controls {
  position: absolute;
  bottom: 15px;
  right: 10px;
  pointer-events: auto;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.street-view-btn {
  margin-bottom: 15px;
}

.street-view-btn i {
  color: #666;
  transition: color 0.2s;
}

.street-view-btn:not(.disabled) i {
  color: #fbbc04;
}

.street-view-btn.disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.control-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  cursor: pointer;
  padding: 0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: background-color 0.2s;
}

.zoom-controls {
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.zoom-controls .control-btn {
  border-radius: 0;
  border: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: none;
}

.zoom-controls .control-btn:last-child {
  border-bottom: none;
}

/* Hide default Leaflet zoom controls */
.leaflet-control-zoom {
  display: none !important;
}

/* Hide attribution */
.leaflet-control-attribution {
  display: none !important;
}
</style>